import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import type { CameraDevice } from "html5-qrcode";
import { Capacitor } from "@capacitor/core";
import { useNavigate } from "react-router-dom";
import { parseTokenFromScan } from "@/lib/parseQrToken";
import * as api from "@/lib/api";

const REGION_ID = "baobao-qr-reader";

type Props = {
  open: boolean;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  onClose: () => void;
  onRedeemed: () => void;
};

function pickCameraId(cameras: CameraDevice[]): string {
  const back = cameras.find((c) => /back|rear|環境|後面|wide|背面/i.test(c.label));
  return (back ?? cameras[0]).id;
}

function isLikelyIosSimulator(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone Simulator|iPad Simulator|Simulator/i.test(navigator.userAgent);
}

export default function QrScannerModal({ open, getIdToken, onClose, onRedeemed }: Props) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string | null>(null);
  const [closingBlocked, setClosingBlocked] = useState(false);
  const [needsTapToStart, setNeedsTapToStart] = useState(true);
  const [starting, setStarting] = useState(false);

  const processingRef = useRef(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  const onCloseRef = useRef(onClose);
  const onRedeemedRef = useRef(onRedeemed);
  const getIdTokenRef = useRef(getIdToken);

  useEffect(() => {
    onCloseRef.current = onClose;
    onRedeemedRef.current = onRedeemed;
    getIdTokenRef.current = getIdToken;
  }, [onClose, onRedeemed, getIdToken]);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (!s) return;
    try {
      await s.stop();
    } catch {
      /* ignore */
    }
    try {
      s.clear();
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!open) {
      void stopScanner();
      setNeedsTapToStart(true);
      setStatus(null);
      setStarting(false);
      processingRef.current = false;
      setClosingBlocked(false);
    }
  }, [open, stopScanner]);

  const handleDecoded = useCallback(
    async (text: string) => {
      if (!open || processingRef.current) return;
      const qrToken = parseTokenFromScan(text);
      if (!qrToken) return;

      processingRef.current = true;
      setClosingBlocked(true);
      setStatus(null);

      await stopScanner();
      setNeedsTapToStart(true);

      try {
        const idToken = await getIdTokenRef.current();
        if (!idToken) {
          setStatus("セッションが無効です。再度ログインしてください。");
          processingRef.current = false;
          setClosingBlocked(false);
          return;
        }
        await api.redeem(idToken, qrToken);
        onRedeemedRef.current();
        onCloseRef.current();
      } catch (e) {
        const msg = e instanceof Error ? e.message : "エラーが発生しました";
        setStatus(msg);
        processingRef.current = false;
        setClosingBlocked(false);
      }
    },
    [open, stopScanner],
  );

  const startScanner = useCallback(async () => {
    setStarting(true);
    setStatus(null);
    await stopScanner();

    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const localhostish = host === "localhost" || host === "127.0.0.1" || host === "[::1]";
    if (typeof window !== "undefined" && !window.isSecureContext && !localhostish) {
      setStatus(
        "このアドレス（http）は iPhone の Safari ではカメラを使えません。https で公開するか、開発時は PC の localhost か、ngrok 等の HTTPS トンネルを利用してください。",
      );
      setStarting(false);
      return;
    }

    if (!document.getElementById(REGION_ID)) {
      setStatus("スキャナを初期化できませんでした。画面を閉じて再度お試しください。");
      setStarting(false);
      return;
    }

    try {
      const scanner = new Html5Qrcode(REGION_ID, /* verbose */ false);
      scannerRef.current = scanner;

      let cameraIdOrConfig: string | MediaTrackConstraints;
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras?.length) {
          cameraIdOrConfig = pickCameraId(cameras);
        } else {
          if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && isLikelyIosSimulator()) {
            setStatus(
              "iOSシミュレータではカメラ入力を使えません。QR読み取りは実機（iPhone/iPad）でお試しください。",
            );
            scannerRef.current = null;
            return;
          }
          cameraIdOrConfig = { facingMode: "environment" };
        }
      } catch {
        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && isLikelyIosSimulator()) {
          setStatus("iOSシミュレータではカメラ入力を使えません。実機でQR読み取りを確認してください。");
          scannerRef.current = null;
          return;
        }
        cameraIdOrConfig = { facingMode: "environment" };
      }

      const box = Math.min(240, Math.max(160, (typeof window !== "undefined" ? window.innerWidth : 320) - 56));

      await scanner.start(
        cameraIdOrConfig,
        { fps: 10, qrbox: { width: box, height: box } },
        (decoded) => {
          void handleDecoded(decoded);
        },
        () => {},
      );

      setNeedsTapToStart(false);
    } catch (e) {
      const detail = e instanceof Error ? e.message : String(e);
      if (/notallowed|permission|denied/i.test(detail)) {
        setStatus("カメラ権限が拒否されています。iOSの「設定 → BaoBao → カメラ」を許可に変更してください。");
        scannerRef.current = null;
        return;
      }
      setStatus(
        `カメラを起動できませんでした。Safari の「設定 → Safari → カメラ」でこのサイトを許可してください。（${detail}）`,
      );
      scannerRef.current = null;
    } finally {
      setStarting(false);
    }
  }, [handleDecoded, stopScanner]);

  if (!open) return null;

  const insecure =
    typeof window !== "undefined" &&
    !window.isSecureContext &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1";

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center px-4"
      style={{ background: "rgba(0,0,0,0.92)" }}
    >
      <button
        type="button"
        onClick={() => !closingBlocked && onClose()}
        className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full cursor-pointer disabled:opacity-40"
        style={{ background: "rgba(212,175,55,0.15)", border: "1px solid rgba(212,175,55,0.3)" }}
        disabled={closingBlocked}
        aria-label="閉じる"
      >
        <i className="ri-close-line text-xl" style={{ color: "#d4af37" }}></i>
      </button>

      <p className="text-xs tracking-widest mb-4" style={{ color: "rgba(212,175,55,0.7)", fontFamily: "Montserrat, sans-serif" }}>
        QR SCANNER
      </p>

      {insecure && (
        <p className="mb-3 text-xs text-center max-w-sm" style={{ color: "#e8c547" }}>
          現在の URL はカメラに必要な「安全な接続」ではありません。iPhone では QR 読取が動かないことがあります。
        </p>
      )}

      <div
        className="relative w-full max-w-sm rounded-2xl overflow-hidden"
        style={{ border: "1px solid rgba(212,175,55,0.25)" }}
      >
        <div id={REGION_ID} className="w-full min-h-[260px] bg-black relative" />

        {needsTapToStart && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4"
            style={{ background: "rgba(0,0,0,0.75)" }}
          >
            <p className="text-xs text-center" style={{ color: "rgba(244,229,195,0.85)" }}>
              iPhone では、ここをタップしてカメラの許可を求める必要があります。
            </p>
            <button
              type="button"
              onClick={() => void startScanner()}
              disabled={starting || insecure}
              className="px-6 py-3 rounded-xl text-sm font-bold disabled:opacity-40"
              style={{
                background: "linear-gradient(135deg, #b8860b 0%, #d4af37 100%)",
                color: "#1a1a2e",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              {starting ? "起動中…" : "カメラを開始"}
            </button>
          </div>
        )}
      </div>

      {status && (
        <p className="mt-4 text-sm text-center max-w-sm" style={{ color: "#ff8080" }}>
          {status}
        </p>
      )}

      <p className="mt-4 text-sm text-center max-w-sm" style={{ color: "rgba(244,229,195,0.6)", fontFamily: "Noto Sans JP, sans-serif" }}>
        店舗タブレットのQRコードに
        <br />
        カメラを向けてください
      </p>

      <button
        type="button"
        onClick={() => {
          if (closingBlocked) return;
          onClose();
          navigate("/");
        }}
        disabled={closingBlocked}
        className="mt-4 px-5 py-2.5 rounded-xl text-sm font-bold disabled:opacity-40"
        style={{
          background: "rgba(212,175,55,0.15)",
          border: "1px solid rgba(212,175,55,0.3)",
          color: "#f4e5c3",
        }}
      >
        カメラを閉じてホームへ
      </button>
    </div>
  );
}
